import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isSpreadsheetFile, setHandoffFile } from '../../services/chat/chatImportHandoff';
import { useChat } from '../../contexts/ChatContext';

/**
 * GlobalSpreadsheetDrop — arrastrar una planilla a CUALQUIER lado de la app
 * abre el importador de animales (no hace falta apuntar al chat ni al botón).
 *
 * El archivo no pasa por el modelo: mismo handoff que el 📎 del chat
 * (chatImportHandoff) → /animals?open_import=1 → ImportAnimalsModal con el
 * archivo ya cargado. Si hay un chat activo, su sessionId viaja para que el
 * AGENTE sepa de la importación cuando el usuario vuelva al chat.
 *
 * No interfiere con dropzones propios (input del chat, dropzone del importador):
 * si el drop cae dentro de ellos, se lo dejamos (guard por target).
 */
const GlobalSpreadsheetDrop: React.FC = () => {
  const navigate = useNavigate();
  const { getActiveSessionId } = useChat();
  const [dragging, setDragging] = useState(false);
  // dragleave del window dispara al pasar por hijos: ocultar por timeout.
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const traeArchivos = (e: DragEvent) =>
      Array.from(e.dataTransfer?.types || []).includes('Files');

    // Un drop sobre un elemento que lo maneja solo (textarea del chat, dropzone
    // del importador, inputs de archivo): no lo robamos.
    const targetPropio = (e: DragEvent) => {
      const el = e.target as HTMLElement | null;
      return !!(el && el.closest && el.closest('textarea, input, [data-ai-dropzone]'));
    };

    const onDragOver = (e: DragEvent) => {
      if (!traeArchivos(e) || targetPropio(e)) return;
      e.preventDefault(); // sin esto el browser abre el archivo
      if (hideTimer.current) clearTimeout(hideTimer.current);
      setDragging(true);
    };
    const onDragLeave = () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(() => setDragging(false), 150);
    };
    const onDrop = (e: DragEvent) => {
      setDragging(false);
      if (targetPropio(e)) return;
      const file = e.dataTransfer?.files?.[0];
      if (!file) return;
      e.preventDefault();
      if (isSpreadsheetFile(file)) {
        setHandoffFile(file, getActiveSessionId() || undefined);
        navigate('/animals?open_import=1&from=chat');
      }
      // No-planilla: no hacemos nada (el browser no lo abre porque hubo
      // preventDefault sólo con planilla — el resto cae en comportamiento normal).
    };

    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [navigate, getActiveSessionId]);

  if (!dragging) return null;
  return (
    <div
      className="fixed inset-0 z-[999] bg-forest/10 backdrop-blur-[2px] flex items-center justify-center pointer-events-none"
      aria-hidden="true"
    >
      <div className="border-2 border-dashed border-forest bg-paper rounded-2xl px-8 py-6 shadow-lift text-center">
        <p className="font-display text-xl font-semibold text-ink">Soltá la planilla para importar animales 📊</p>
        <p className="mt-1 text-sm text-sage">Se abre el importador con el archivo ya cargado (.xlsx / .csv)</p>
      </div>
    </div>
  );
};

export default GlobalSpreadsheetDrop;
